import AWSAppSyncClient from "aws-appsync";
import { NormalizedCacheObject } from "apollo-cache-inmemory";
import { ApolloQueryResult, FetchPolicy } from "apollo-client";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { FieldName, FieldValues } from "../Schema";
import { getGraphqlDocument, SchemaClientKinds, Source } from "./Source";

export type FieldListResult<T> = {items: T[]; nextToken?: string};

export type FieldListResults<T, N extends FieldName = FieldName> = Record<N, FieldListResult<T> | null>;

export type FieldSingleResult<T> = T;

export type FieldSingleResults<T, N extends FieldName = FieldName> = Record<N, FieldSingleResult<T> | null>;

export type FieldResult<T> = FieldSingleResult<T> | FieldListResult<T>;

export type FieldResults<T, N extends FieldName = FieldName> = Record<N, FieldResult<T> | null>;

export const getRootFieldName = <T, V = FieldValues>(
  document: TypedDocumentNode<T, V>
): string | null => {

  const operationDefinition = document.definitions.find(
    (definition) => definition.kind === "OperationDefinition"
  );

  if (operationDefinition?.selectionSet) {
    const firstField = operationDefinition.selectionSet.selections.find(
      (selection) => selection.kind === "Field"
    );

    if (firstField && firstField.kind === "Field") {
      return (
        firstField.alias ?
          firstField.alias.value :
          firstField.name.value
      );
    }
  }
  return null;
}

export const isFieldResultList = <T>(
  value: FieldResult<T>
): value is FieldListResult<T> => {
  return value && value.hasOwnProperty("items") &&
    Array.isArray((value as FieldListResult<T>).items);
}

/**
 * The read surface the schema needs from a client: the two query methods its external
 * sources call, and nothing else.
 *
 * Field definitions depend on this rather than on {@link GraphqlClient} so a schema can be
 * built somewhere with no AppSync client at all — a Lambda reading master data straight
 * from DynamoDB gets {@link DynamoDbClient} instead. `mutate` is deliberately absent:
 * preparing a schema only ever reads.
 *
 * A source ({@link Source}) carries one definition per client kind, so the getters name
 * the source and the implementation picks the definition matching its own {@link kind}.
 */
export interface SchemaClient {
  /** Which definition of a source this client reads. */
  readonly kind: SchemaClientKinds;

  query<T, V = FieldValues>(
    source: Source<T, V>,
    variables?: V,
  ): Promise<T | null>;

  queryList<T, V = FieldValues>(
    source: Source<T[], V>,
    variables?: V,
  ): Promise<T[] | null>;
}

export class GraphqlClient implements SchemaClient {

  public readonly kind = SchemaClientKinds.GRAPHQL;

  client: AWSAppSyncClient<NormalizedCacheObject>;

  /**
   * Fetch policy applied to every query. Defaults to `network-only` (always
   * fresh). Pass `cache-first` when the same reference/master data is read many
   * times in one session (e.g. the audit trail preparing a display per record),
   * so repeated identical queries are served from the Apollo cache.
   */
  fetchPolicy: FetchPolicy;

  constructor(
    client: AWSAppSyncClient<NormalizedCacheObject>,
    fetchPolicy: FetchPolicy = "network-only"
  ) {
    this.client = client;
    this.fetchPolicy = fetchPolicy;
  }

  public async queryList<T, V = FieldValues>(
    source: Source<T[], V>,
    variables: V = {} as V,
  ): Promise<T[] | null> {
    const query = getGraphqlDocument<T[], V>(source);
    if (!query) {
      throw new Error("Source has no GraphQL query");
    }
    const fieldName = getRootFieldName<T[], V>(query);
    if (!fieldName) {
      throw new Error("Query field name not found");
    }

    let data: T[] = [];
    let nextToken;
    let result: ApolloQueryResult<FieldListResults<T, string>>;
    do {
      result = await this.client.query<FieldListResults<T, string>>({
        query,
        variables: { ...variables, nextToken },
        fetchPolicy: this.fetchPolicy,
      });
      if (result.errors) {
        throw new Error(`GraphQL query error: ${result.errors.map((e) => e.message).join(", ")}`);
      }
      const fieldResult = result.data?.[fieldName] ?? null;
      if (fieldResult && isFieldResultList(fieldResult)) {
        data = [...data, ...fieldResult.items];
        nextToken = fieldResult.nextToken;
      }
    } while (nextToken);

    return data ?? null;
  }

  public async query<T, V = FieldValues>(
    source: Source<T, V>,
    variables: V = {} as V,
  ): Promise<T | null> {
    const query = getGraphqlDocument<T, V>(source);
    if (!query) {
      throw new Error("Source has no GraphQL query");
    }
    const fieldName = getRootFieldName<T, V>(query);
    if (!fieldName) {
      throw new Error("Query field name not found");
    }

    const result = await this.client.query<FieldSingleResults<T, string>>({
      query,
      variables: variables as object,
      fetchPolicy: this.fetchPolicy,
    });
    if (result?.errors) {
      throw new Error(`GraphQL query error: ${result.errors.map((e) => e.message).join(", ")}`);
    }
    return result?.data?.[fieldName] ?? null;
  }

  public async mutate<T, V = FieldValues>(
    mutation: TypedDocumentNode<T, V>,
    variables: V,
  ): Promise<T[] | T | null> {
    const fieldName = getRootFieldName<T, V>(mutation);
    if (!fieldName) {
      throw new Error("Mutation field name not found");
    }
    const result = await this.client.mutate<FieldResults<T, string>>({
      mutation,
      variables: variables as object,
    });

    const fieldResult = result.data?.[fieldName] ?? null;
    if (fieldResult) {
      if (isFieldResultList(fieldResult)) {
        return fieldResult.items ?? null;
      } else {
        return fieldResult;
      }
    }
    return null;
  }
}
